import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, TrendingUp, ArrowUpRight, ArrowDownRight, Download, Calendar, Filter, Wallet, PiggyBank, Receipt, CreditCard, BarChart3, ChevronRight, ExternalLink } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MOCK_TRANSACTIONS = [
  { id: 1, type: 'settlement', desc: 'DMCA Settlement — @ViralStealz (YouTube)', amount: 2400, date: '2026-03-25', status: 'completed' },
  { id: 2, type: 'royalty', desc: 'Marketplace License — Cinematic Score Pack', amount: 149, date: '2026-03-24', status: 'completed' },
  { id: 3, type: 'settlement', desc: 'DMCA Settlement — MixMaster101 (TikTok)', amount: 1800, date: '2026-03-22', status: 'completed' },
  { id: 4, type: 'bounty', desc: 'Bounty Reward — Content ID Match (Instagram)', amount: 680, date: '2026-03-20', status: 'completed' },
  { id: 5, type: 'settlement', desc: 'Velvet Hammer — BeatJacker (SoundCloud)', amount: 3200, date: '2026-03-18', status: 'pending' },
  { id: 6, type: 'royalty', desc: 'Marketplace License — Lo-Fi Beat Tape', amount: 49, date: '2026-03-17', status: 'completed' },
  { id: 7, type: 'payout', desc: 'Payout to Bank Account ****4821', amount: -5000, date: '2026-03-15', status: 'completed' },
  { id: 8, type: 'bounty', desc: 'Bounty Reward — Video Match (Twitter/X)', amount: 420, date: '2026-03-12', status: 'completed' },
  { id: 9, type: 'settlement', desc: 'DMCA Settlement — ContentFarm (Facebook)', amount: 1650, date: '2026-03-10', status: 'completed' },
  { id: 10, type: 'royalty', desc: 'Marketplace License — Drone Footage Pack', amount: 199, date: '2026-03-08', status: 'completed' },
];

const MONTHLY_REVENUE = [
  { month: 'Oct', settlements: 4200, royalties: 890, bounties: 1200 },
  { month: 'Nov', settlements: 6800, royalties: 1340, bounties: 1800 },
  { month: 'Dec', settlements: 5100, royalties: 2100, bounties: 2400 },
  { month: 'Jan', settlements: 8900, royalties: 1800, bounties: 3200 },
  { month: 'Feb', settlements: 7200, royalties: 2400, bounties: 2800 },
  { month: 'Mar', settlements: 10564, royalties: 3100, bounties: 4100 },
];

const TYPE_STYLES = {
  settlement: { bg: 'rgba(0,240,255,0.08)', color: '#00F0FF', label: 'Settlement' },
  royalty: { bg: 'rgba(139,92,246,0.08)', color: '#8B5CF6', label: 'Royalty' },
  bounty: { bg: 'rgba(245,158,11,0.08)', color: '#F59E0B', label: 'Bounty' },
  payout: { bg: 'rgba(255,0,229,0.08)', color: '#FF00E5', label: 'Payout' },
};

export default function TreasuryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [txFilter, setTxFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('6mo');

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  const totalBalance = 17764;
  const pendingAmount = 3200;
  const totalPaidOut = 12400;
  const monthlyGrowth = 18.4;

  const filtered = txFilter === 'all' ? MOCK_TRANSACTIONS : MOCK_TRANSACTIONS.filter(t => t.type === txFilter);
  const maxBar = Math.max(...MONTHLY_REVENUE.map(m => m.settlements + m.royalties + m.bounties));

  return (
    <div className="min-h-screen bg-texture" data-testid="treasury-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <Wallet size={14} className="text-[--ch-cyan]" />
            <span className="font-label text-[--ch-cyan]">FINANCIAL COMMAND CENTER</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black" data-testid="treasury-title">
            <span className="text-white">Q-Ledger </span><span className="neon-cyan">Treasury</span>
          </h1>
          <p className="text-sm text-[--ch-text-secondary] mt-1">
            Real-time earnings, settlement tracking, and payout management.
          </p>
        </div>

        {/* Big Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fade-in-up stagger-1">
          {[
            { label: 'Available Balance', value: `$${totalBalance.toLocaleString()}`, icon: DollarSign, color: '#00F0FF', sub: `+$${(totalBalance * 0.12).toFixed(0)} this month` },
            { label: 'Pending Settlements', value: `$${pendingAmount.toLocaleString()}`, icon: Receipt, color: '#F59E0B', sub: '1 case awaiting payment' },
            { label: 'Total Paid Out', value: `$${totalPaidOut.toLocaleString()}`, icon: CreditCard, color: '#8B5CF6', sub: 'Lifetime earnings withdrawn' },
            { label: 'Monthly Growth', value: `+${monthlyGrowth}%`, icon: TrendingUp, color: '#22C55E', sub: 'vs. previous month' },
          ].map((stat, i) => (
            <div key={i} className="ch-card p-5 relative overflow-hidden group" data-testid={`treasury-stat-${i}`}>
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-[0.04] blur-[40px]"
                style={{ background: stat.color }} />
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                style={{ background: `${stat.color}12`, border: `1px solid ${stat.color}20` }}>
                <stat.icon size={18} style={{ color: stat.color }} />
              </div>
              <p className="text-xs text-[--ch-text-secondary] mb-1">{stat.label}</p>
              <p className="text-2xl font-black" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-[11px] text-[--ch-text-secondary] mt-1">{stat.sub}</p>
            </div>
          ))}
        </div>

        {/* Revenue Chart */}
        <div className="ch-card p-5 mb-8 animate-fade-in-up stagger-2" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
              <BarChart3 size={14} className="text-[--ch-cyan]" /> Revenue Breakdown
            </h3>
            <div className="flex gap-2">
              {['3mo', '6mo', '1y'].map(r => (
                <button key={r} onClick={() => setTimeRange(r)}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold transition-all ${
                    timeRange === r
                      ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan] border border-[--ch-cyan]'
                      : 'text-[--ch-text-secondary] border border-[--ch-border] hover:border-gray-500'
                  }`}>
                  {r.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-4 mb-4">
            {[
              { label: 'Settlements', color: '#00F0FF' },
              { label: 'Royalties', color: '#8B5CF6' },
              { label: 'Bounties', color: '#F59E0B' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} />
                <span className="text-[10px] text-[--ch-text-secondary]">{l.label}</span>
              </div>
            ))}
          </div>

          {/* Bar Chart */}
          <div className="flex items-end gap-2 h-40">
            {MONTHLY_REVENUE.map((m, i) => {
              const total = m.settlements + m.royalties + m.bounties;
              const h = (total / maxBar) * 100;
              const sH = (m.settlements / total) * h;
              const rH = (m.royalties / total) * h;
              const bH = (m.bounties / total) * h;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group/bar">
                  <div className="w-full flex flex-col items-stretch rounded-t-md overflow-hidden relative"
                    style={{ height: `${h}%`, minHeight: '8px' }}>
                    <div style={{ height: `${(bH/h)*100}%`, background: '#F59E0B', opacity: 0.8 }} />
                    <div style={{ height: `${(rH/h)*100}%`, background: '#8B5CF6', opacity: 0.8 }} />
                    <div style={{ height: `${(sH/h)*100}%`, background: '#00F0FF', opacity: 0.8 }} />
                    <div className="absolute inset-0 opacity-0 group-hover/bar:opacity-100 transition-opacity bg-white/5 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-white">${(total/1000).toFixed(1)}k</span>
                    </div>
                  </div>
                  <span className="text-[10px] text-[--ch-text-secondary]">{m.month}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Transaction History */}
        <div className="ch-card overflow-hidden animate-fade-in-up stagger-3" data-testid="transaction-list">
          <div className="p-4 border-b border-[--ch-border] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
              <Receipt size={14} className="text-[--ch-magenta]" /> Transaction History
            </h3>
            <div className="flex gap-2 overflow-x-auto">
              {[
                { id: 'all', label: 'All' },
                { id: 'settlement', label: 'Settlements' },
                { id: 'royalty', label: 'Royalties' },
                { id: 'bounty', label: 'Bounties' },
                { id: 'payout', label: 'Payouts' },
              ].map(f => (
                <button key={f.id} onClick={() => setTxFilter(f.id)}
                  data-testid={`tx-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold whitespace-nowrap transition-all ${
                    txFilter === f.id
                      ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan] border border-[--ch-cyan]'
                      : 'text-[--ch-text-secondary] border border-[--ch-border] hover:border-gray-500'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y divide-[--ch-border]">
            {filtered.map(tx => {
              const style = TYPE_STYLES[tx.type];
              return (
                <div key={tx.id} className="p-4 flex items-center gap-4 hover:bg-[--ch-bg-card-hover] transition-colors" data-testid={`tx-${tx.id}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: style.bg }}>
                    {tx.amount >= 0 ? (
                      <ArrowUpRight size={16} style={{ color: style.color }} />
                    ) : (
                      <ArrowDownRight size={16} style={{ color: style.color }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{tx.desc}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] px-2 py-0.5 rounded font-bold"
                        style={{ background: style.bg, color: style.color }}>
                        {style.label}
                      </span>
                      <span className="text-xs text-[--ch-text-secondary]">{tx.date}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${tx.amount >= 0 ? 'text-[--ch-success]' : 'text-[--ch-magenta]'}`}>
                      {tx.amount >= 0 ? '+' : ''}${Math.abs(tx.amount).toLocaleString()}
                    </p>
                    <p className={`text-[10px] font-bold ${tx.status === 'completed' ? 'text-[--ch-success]' : 'text-[--ch-warning]'}`}>
                      {tx.status === 'completed' ? 'Completed' : 'Pending'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Payout CTA */}
          <div className="p-5 border-t border-[--ch-border] flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold">Ready to withdraw?</p>
              <p className="text-xs text-[--ch-text-secondary]">Available: <span className="text-[--ch-cyan] font-bold">${totalBalance.toLocaleString()}</span></p>
            </div>
            <button data-testid="request-payout-btn"
              className="btn-neon px-6 py-3 rounded-full text-sm font-semibold flex items-center gap-2">
              <Download size={14} /> Request Payout <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
